import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { Box } from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import { AssignmentInd } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Card, FormGroup } from "@cofano/cck";

import { intl } from "LocaleWrapper";
import { gql } from "__generated__/gql";
import { CharterPdfType, SalesCharterStatus } from "__generated__/enums";
import { SalesCharter } from "types/index";
import { useConfirm } from "components/hooks/useConfirm";
import { DataFormat, Fields, hasOptionalFieldPermission, usePermission } from "imports/Utils";
import { useStoreState } from "ezStore";

import SalesCharterStatusChanger from "view/charters/components/status/SalesCharterStatusChanger";
import { DetailsBlock, Height } from "../Blocks";
import SalesCharterCreateEditDialog from "./SalesCharterCreateEditDialog";
import { RelationLink } from "utils/LinkUtils";
import { FormattedDate } from "utils/DateUtils";
import ExpandableText from "components/text/ExpandableText";
import { PdfButton } from "view/charters/dossier/ship/ShipDetailsBlock";
import CharterSubtypeTag from "view/charters/components/tags/CharterSubtypeTag";

type SalesCharterDetailsBlockProps = {
	charter: SalesCharter;
	color: string;
	refetch: () => void;
	onDelete: () => void;
};

const SalesCharterDetailsBlock = ({ charter, color, refetch, onDelete }: SalesCharterDetailsBlockProps) => {

	const confirm = useConfirm();
	const { isReadOnly } = usePermission();

	const session = useStoreState(store => store.session);
	const showDossierNumber = hasOptionalFieldPermission(session, Fields.SALES_CHARTER, "dossierNumber");

	const [ dialogOpen, openDialog ] = useState(false);

	const [ deleteMutation, { loading } ] = useMutation(DELETE_CHARTER);

	const deleteCharter = (deleteChildren: boolean) => {
		deleteMutation({
			variables: {
				id: charter.id,
				deleteChildren: deleteChildren,
				deleteVoyageIfEmpty: true,
			},
		}).then(() => {
			onDelete();
		});
	};

	return (
		<React.Fragment>
			<DetailsBlock height={Height.Fill}>
				<Card
					color={color}
					icon={<AssignmentInd sx={{ color: color }} />}
					title={charter.charterNumber}
					subtitle={<RelationLink relation={charter.debtor} />}
					actions={[ {
						hidden: isReadOnly,
						render: () => (
							<span style={{ marginRight: 4 }}>
								<SalesCharterStatusChanger
									/* @ts-expect-error */
									charter={charter}
									onCompleted={refetch}
								/>
							</span>
						),
					}, {
						render: () => <PdfButton charter={charter} type={CharterPdfType.NominationToCustomer} />,
					}, {
						icon: <EditIcon fontSize="small" />,
						hidden: isReadOnly,
						onClick: () => openDialog(true),
					}, {
						icon: <DeleteIcon fontSize="small" />,
						disabled: loading,
						hidden: isReadOnly || charter.status?.name !== SalesCharterStatus.Draft,
						onClick: () => {
							confirm({
								type: "DELETE",
								confirmText: intl.formatMessage({ id: "DELETE_CHARTER_AND_CARGOS" }),
							}).then(() => {
								deleteCharter(true);
							});
						},
					} ]}
					disablePadding>
					<Box sx={{
						borderTop: 1,
						borderColor: "divider",
						height: "100%",
					}}>
						<div style={{ padding: 16 }}>
							<FormGroup label={intl.formatMessage({ id: "TYPE" })} labelPlacement="left-center" overflow="hidden">
								<CharterSubtypeTag type={charter.subtype} />
							</FormGroup>

							<FormGroup label={intl.formatMessage({ id: "CONTRACTOR" })} labelPlacement="left-center" overflow="hidden">
								<RelationLink relation={charter.creditor} />
							</FormGroup>

							<FormGroup label={intl.formatMessage({ id: "CUSTOMER" })} labelPlacement="left-center" overflow="hidden">
								<RelationLink relation={charter.debtor} />
							</FormGroup>

							{charter.broker && (
								<FormGroup label={intl.formatMessage({ id: "BROKER" })} labelPlacement="left-center" overflow="hidden">
									<RelationLink relation={charter.broker} />
								</FormGroup>
							)}

							<FormGroup label={intl.formatMessage({ id: "CARGO_CHARTER_MINIMUM_SHIP_NOMINATION_DAYS" })} labelPlacement="left-center" overflow="visible">
								{charter.minimumShipNominationDays || "-"}
							</FormGroup>

							<FormGroup label={intl.formatMessage({ id: "CARGO_CHARTER_SHIP_NOMINATION_PERIOD_REGIME" })} labelPlacement="left-center" overflow="visible">
								{charter.shipNominationPeriodRegime ? intl.formatMessage({ id: charter.shipNominationPeriodRegime as string }) : "-"}
							</FormGroup>

							<FormGroup label={intl.formatMessage({ id: "CARGO_CHARTER_SHORT_NOTICE" })} labelPlacement="left-center" overflow="hidden">
								{charter.shortNotice ? DataFormat.booleanAsText(charter.shortNotice) : "-"}
							</FormGroup>

							<FormGroup label={intl.formatMessage({ id: "CUSTOMER_REFERENCE" })} labelPlacement="left-center" overflow="hidden">
								{DataFormat.remarks(charter.customerReference, "-")}
							</FormGroup>

							{showDossierNumber && (
								<FormGroup label={intl.formatMessage({ id: "DOSSIER_NUMBER" })} labelPlacement="left-center" overflow="hidden">
									{DataFormat.remarks(charter.dossierNumber, "-")}
								</FormGroup>
							)}

							<FormGroup
								label={intl.formatMessage({ id: "CARGO_CHARTER_CUSTOMER_NOTES" })}
								labelPlacement="left-center"
								overflow="hidden" >
								<ExpandableText
									text={charter.customerNotes}
								/>
							</FormGroup>

							<FormGroup
								label={intl.formatMessage({ id: "CARGO_CHARTER_INTERNAL_NOTES" })}
								labelPlacement="left-center"
								overflow="visible" >
								<ExpandableText
									text={charter.internalNotes}
								/>
							</FormGroup>

							<FormGroup label={intl.formatMessage({ id: "BL_DATE" })} labelPlacement="left-center" overflow="hidden">
								{charter.blDate ? <FormattedDate date={charter.blDate } format="ll"  /> : "-" }
							</FormGroup>
						</div>
					</Box>
				</Card>
			</DetailsBlock>

			<SalesCharterCreateEditDialog
				open={dialogOpen}
				onClose={() => openDialog(false)}
				charter={charter}
				refetch={refetch} />
		</React.Fragment>
	);
};

const DELETE_CHARTER = gql(`#graphql
mutation deleteSalesCharter_charterBlock($id: Long!, $deleteChildren: Boolean!, $deleteVoyageIfEmpty: Boolean) {
	deleteCharter(id: $id, deleteChildren: $deleteChildren, deleteVoyageIfEmpty: $deleteVoyageIfEmpty)
}
`);

export default SalesCharterDetailsBlock;
